import React, { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import PasswordField from "@/components/PasswordField";
import { toast } from "sonner";
import { AtSign, LogIn, UserPlus, ShieldCheck } from "@/lib/icons";
import { useSeo } from "@/lib/seo";
import EmailDeliveryNotice, { withEmailDeliveryNote } from "@/components/EmailDeliveryNotice";

export default function Login() {
  const { user, memberLogin, verifyLoginOtp, resendLoginOtp } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useSeo({ title: "Sign In | PRONET", noindex: true });

  // Step 2: new-device email OTP (only triggered when the backend flags this browser as unrecognized)
  const [otpToken, setOtpToken] = useState("");
  const [otpMessage, setOtpMessage] = useState("");
  const [otp, setOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  if (user && user.role === "member") return <Navigate to="/account" replace />;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const afterLogin = (data) => {
    toast.success("Welcome back!");
    if (data?.must_change_password) {
      nav("/account/first-login", { replace: true });
    } else {
      nav("/account", { replace: true });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const data = await memberLogin(form.identifier.trim(), form.password);
      if (data?.otp_required) {
        setOtpToken(data.otp_token);
        setOtpMessage(data.message || "Enter the code we emailed you.");
        setCooldown(60);
      } else {
        afterLogin(data);
      }
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const data = await verifyLoginOtp(otpToken, otp);
      afterLogin(data);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    setErr("");
    try {
      const data = await resendLoginOtp(otpToken);
      setOtpToken(data.otp_token);
      setOtpMessage(data.message || "A new code was sent.");
      setCooldown(60);
      toast.success(withEmailDeliveryNote("Code resent."));
    } catch (e) { setErr(formatError(e)); }
  };

  if (otpToken) {
    return (
      <div className="relative container-tight py-12 md:py-20 max-w-md">
        <div className="blobs-layer">
          <div className="blob blob-violet w-[400px] h-[400px] -top-20 -left-20 opacity-45" />
          <div className="blob blob-magenta w-[380px] h-[380px] bottom-0 right-0 opacity-35" style={{ animationDelay: "-9s" }} />
        </div>
        <form onSubmit={submitOtp} className="relative glass rounded-3xl p-6 sm:p-10 fade-up-slow">
          <div className="overline text-fuchsia-300">New device detected</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl leading-none text-white">Verify it's <span className="italic text-gradient">you.</span></h1>
          <p className="mt-3 text-white/60 text-sm">{otpMessage}</p>
          <EmailDeliveryNotice className="mt-4" />

          <div className="mt-8">
            <label className="overline text-white/60 text-[10px]">Verification code</label>
            <Input required data-testid="login-otp" maxLength={6} inputMode="numeric" autoComplete="one-time-code"
              className="mt-2 h-12 text-center text-2xl tracking-[0.4em] bg-white/5 border-white/10 text-white"
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} />
          </div>

          {err && <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="login-error">{err}</div>}

          <button type="submit" disabled={busy || otp.length !== 6} data-testid="login-otp-submit"
            className="mt-8 btn-glow w-full h-12 rounded-full text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <ShieldCheck size={15} /> {busy ? "Verifying…" : "Verify & sign in"}
          </button>
          <button type="button" disabled={cooldown > 0 || busy} onClick={resend} data-testid="login-otp-resend"
            className="mt-3 w-full text-xs text-white/50 hover:text-fuchsia-300 disabled:opacity-40">
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="relative container-tight py-12 md:py-20 max-w-md">
      <div className="blobs-layer">
        <div className="blob blob-violet w-[400px] h-[400px] -top-20 -left-20 opacity-45" />
        <div className="blob blob-magenta w-[380px] h-[380px] bottom-0 right-0 opacity-35" style={{ animationDelay: "-9s" }} />
      </div>

      <form onSubmit={submit} className="relative glass rounded-3xl p-6 sm:p-10 fade-up-slow">
        <div className="overline text-fuchsia-300">Member sign in</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl leading-none text-white">Welcome <span className="italic text-gradient">back.</span></h1>
        <p className="mt-3 text-white/60 text-sm">Sign in with your username, email or 10-digit mobile.</p>

        <div className="mt-8 space-y-4">
          <div>
            <label className="overline text-white/60 text-[10px]">Username, email or mobile</label>
            <div className="relative mt-2">
              <AtSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input required data-testid="login-identifier"
                autoComplete="username"
                className="pl-9 h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                placeholder="Type here"
                value={form.identifier}
                onChange={(e) => setForm({ ...form, identifier: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="overline text-white/60 text-[10px]">Password</label>
            <div className="mt-2">
              <PasswordField testid="login-password" autoComplete="current-password"
                className="h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                value={form.password}
                onChange={(v) => setForm({ ...form, password: v })} />
            </div>
            <div className="mt-2 flex justify-end">
              <Link to="/forgot-password" data-testid="login-forgot"
                className="text-[10px] uppercase tracking-widest text-white/50 hover:text-fuchsia-300">
                Forgot password?
              </Link>
            </div>
          </div>
        </div>

        {err && <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="login-error">{err}</div>}

        {err === "Invalid credentials" && (
          <p className="mt-2 text-xs leading-relaxed text-white/45">
            If your account was imported or you have not created a password yet, use{" "}
            <Link to="/forgot-password" className="font-semibold text-fuchsia-300 hover:underline">Forgot password</Link>
            {" "}to set one.
          </p>
        )}

        <button type="submit" disabled={busy} data-testid="login-submit"
          className="mt-8 btn-glow w-full h-12 rounded-full text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <LogIn size={15} /> {busy ? "Signing in…" : "Sign in"}
        </button>

        <div className="mt-6 text-sm text-white/50 text-center">
          New here?{" "}
          <Link to="/register" className="text-fuchsia-300 font-semibold inline-flex items-center gap-1">
            <UserPlus size={13} /> Create an account
          </Link>
        </div>
      </form>
    </div>
  );
}
